import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, X, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { useElearningCartStats, useElearningCartActions } from '../../stores/useElearningCartStore';

export function MiniCartPreview() {
  const { items } = useElearningCartStats();
  const { removeItem } = useElearningCartActions();
  const [open, setOpen] = useState(false);

  // Fermer le mini-panier lorsque l'utilisateur clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (open && !target.closest('.mini-cart-container')) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="fixed top-32 md:top-24 right-4 md:right-10 z-50 mini-cart-container">
      <button
        aria-label="Aperçu du panier de formations"
        className="relative bg-[#002366]/90 rounded-full p-3 shadow-lg hover:bg-[#D4AF37] transition-all duration-300"
        onClick={() => setOpen(v => !v)}
      >
        <ShoppingCart className="w-6 h-6 text-white" />
        {items.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-white rounded-full text-xs px-2 py-0.5">
            {items.length}
          </span>
        )}
      </button>
      
      {open && (
        <div className="mt-2 w-80 bg-white/95 backdrop-blur-xl text-[#002366] rounded-xl shadow-2xl p-4 border border-[#D4AF37]/20 relative">
          <button
            aria-label="Fermer le mini-panier"
            className="absolute top-2 right-2 text-[#002366]/70 hover:text-[#D4AF37] text-xl font-bold transition-colors"
            onClick={() => setOpen(false)}
            tabIndex={0}
          >
            <X size={16} />
          </button>
          
          <div className="flex justify-between items-center mb-2 pr-6">
            <span className="font-bold">Mes formations</span>
            <Link to="/elearning/cart" className="text-[#D4AF37] underline text-sm hover:text-[#002366] transition-colors">
              Voir tout
            </Link>
          </div>
          
          {items.length === 0 ? (
            <div className="text-[#002366]/60 text-sm py-2">Votre panier est vide.</div>
          ) : (
            <ul className="divide-y divide-[#D4AF37]/20 max-h-80 overflow-y-auto">
              {items.map(item => (
                <li key={item.course_id} className="py-2 grid grid-cols-6 items-center gap-2">
                  {/* Image 1/6 */}
                  <div className="col-span-1 flex items-center">
                    {item.thumbnail_url ? (
                      <img
                        src={item.thumbnail_url}
                        alt={item.title}
                        className="w-10 h-10 rounded object-cover bg-[#002366]/10 border border-[#D4AF37]/30"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400';
                        }}
                        aria-label={`Image de ${item.title}`}
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center rounded bg-[#002366]/10 border border-[#D4AF37]/30">
                        <BookOpen className="w-5 h-5 text-[#002366]/60" />
                      </div>
                    )}
                  </div>
                  
                  {/* Titre + quantité 3/6 */}
                  <div className="col-span-3 min-w-0">
                    <div className="font-semibold truncate text-sm">{item.title}</div>
                    <div className="text-xs text-[#002366]/70">Formation</div>
                  </div>
                  
                  {/* Prix 1/6 */}
                  <div className="col-span-1 text-sm font-bold text-[#D4AF37] text-right">
                    {new Intl.NumberFormat('fr-FR').format(item.price)} {item.currency}
                  </div>
                  
                  {/* Icône suppression 1/6 */}
                  <div className="col-span-1 flex justify-end">
                    <Button
                      aria-label="Retirer du panier"
                      size="icon"
                      variant="ghost"
                      className="text-[#002366]/40 hover:text-red-500 h-8 w-8 transition-colors"
                      onClick={() => removeItem(item.course_id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          
          {items.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#D4AF37]/20">
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium">Total:</span>
                <span className="font-bold text-[#D4AF37]">
                  {new Intl.NumberFormat('fr-FR').format(
                    items.reduce((total, item) => total + item.price, 0)
                  )} {items[0]?.currency || 'FCFA'}
                </span>
              </div>
              <Link 
                to="/elearning/checkout" 
                className="block w-full bg-gradient-to-r from-[#002366] to-[#001a4d] hover:from-[#001a4d] hover:to-[#002366] text-white text-center py-2 rounded-md transition-all duration-300 font-medium"
                onClick={() => setOpen(false)}
              >
                Passer à la caisse
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
